import { addDays, format, subDays } from 'date-fns'
import { isEmpty } from 'lodash'
import queryString from 'query-string'
import * as Yup from 'yup'

import { getTagsWithCategories } from '@/utils/helpers'

let nextRowId = 0

const createRowId = () => window.crypto?.randomUUID?.() || `instant-demo-row-${nextRowId++}`
const createRow = (fields) => ({ id: createRowId(), ...fields })

export const createFieldRow = () => createRow({ fieldName: '', prompt: '' })
export const createCriteriaRow = () => createRow({ criteriaName: '', question: '' })

export const getDefaultPlaybookSettings = () => ({
  playbookMode: 'create',
  playbookName: '',
  selectedPlaybookCid: '',
  notetakerMode: 'create',
  notetakerAutoDecide: true,
  notetakerFields: [createFieldRow()],
})

export const getDefaultScoringSettings = (organizationId = null) => ({
  scorecardMode: organizationId ? 'all_active' : 'create',
  scorecardName: '',
  selectedScorecardConfigId: null,
  scorecardAutoDecide: true,
  scorecardCriteria: [createCriteriaRow()],
})

export const OMNICHANNEL_CHANNELS = ['chat', 'sms', 'email']
export const OMNICHANNEL_CONVERSATIONS_MIN = 1
export const OMNICHANNEL_CONVERSATIONS_MAX = 20
export const OMNICHANNEL_CONVERSATIONS_DEFAULT = 3

export const DISTRIBUTION_SHAPES = ['uniform', 'increasing', 'decreasing', 'random']
export const DISTRIBUTE_OVER_DAYS_MIN = 1
// Trend backfill only samples the last 60 days, so a longer window silently drops calls.
export const DISTRIBUTE_OVER_DAYS_MAX = 60
export const DISTRIBUTE_OVER_DAYS_DEFAULT = 30

export const INITIAL_FORM_STATE = {
  organizationId: null,
  organizationName: '',
  useCase: '',
  insightsGoal: '',
  ...getDefaultPlaybookSettings(),
  ...getDefaultScoringSettings(),
  agentMode: 'create',
  newUsersToCreate: 2,
  generateCalls: false,
  numCalls: 5,
  fastMode: false,
  simulateTrend: false,
  distributeOverDays: DISTRIBUTE_OVER_DAYS_DEFAULT,
  distributionShape: 'uniform',
  selectedUsernames: [],
  userTagIds: [],
  existingAgentFilterTagIds: [],
  generateOmnichannel: false,
  omnichannelChannels: [...OMNICHANNEL_CHANNELS],
  omnichannelConversationsPerChannel: OMNICHANNEL_CONVERSATIONS_DEFAULT,
}

export const INITIAL_SETUP_STATE = {
  progress: 0,
  message: '',
  organizationData: null,
  isComplete: false,
  error: null,
  isSettingUp: false,
  connectionStatus: 'disconnected',
  completionData: null,
}

export const INITIAL_ORG_RESOURCES = {
  hasLoaded: false,
  loading: false,
  userOptions: [],
  playbooks: [],
  scorecards: [],
  tagOptions: [],
}

export const LOCKED_SECTION_MESSAGE = 'Select or enter an organization for setup options'

// Omnichannel threads are seeded at `base_time = now - 24h` and spread
// `index * 11min` per conversation, so they can land hours into yesterday
// and miss the Explorer's "today" default. Pin the omnichannel link to a
// ~7-day window so freshly seeded threads always show up. One `new Date()`
// reference so the range is internally consistent around midnight / DST.
const getDemoDateRangeParams = () => {
  const now = new Date()
  return {
    startDate: format(subDays(now, 7), "yyyy-MM-dd'T00:00:00'xxx"),
    endDate: format(addDays(now, 1), "yyyy-MM-dd'T23:59:59'xxx"),
  }
}

export const getCallExplorerLink = (callIds, organizationId) => {
  if (!callIds || !Array.isArray(callIds)) {
    return '/explorer'
  }

  // No date params: parseParams in callSearch/helpers.js short-circuits
  // when call_ids is present and ignores startDate/endDate. Call lookup
  // by id isn't date-gated, so calls render correctly regardless.
  const params = queryString.stringify({
    call_ids: callIds,
    organizationId: organizationId || undefined,
  })
  return `/explorer?${params}`
}

export const getOmnichannelExplorerLink = (channel, organizationId) => {
  const params = queryString.stringify({
    channel,
    organizationId: organizationId || undefined,
    ...getDemoDateRangeParams(),
  })
  return `/explorer?${params}`
}

export const getOrganizationLink = (organizationId) => `/organizations/${organizationId}`

const byLabel = (a, b) => a.label.localeCompare(b.label)

export const buildOrganizationResources = ({
  userResponse,
  playbooks,
  scorecards,
  tags,
  tagCategories,
}) => {
  const users = userResponse?.users || []
  const userOptions = users
    .map((user) => ({
      value: user.username,
      label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username,
      tagIds: (user.tags || []).map((tag) => tag.id),
    }))
    .filter((user) => user.value)
    .sort(byLabel)

  const playbookOptions = playbooks
    .map((playbook) => ({
      value: playbook.cid,
      label: playbook.name,
    }))
    .sort(byLabel)

  const scorecardOptions = scorecards
    .map((scorecard) => ({
      value: scorecard.id,
      label: scorecard.name,
    }))
    .sort(byLabel)

  return {
    hasLoaded: true,
    loading: false,
    userOptions,
    playbooks: playbookOptions,
    scorecards: scorecardOptions,
    tagOptions: getTagsWithCategories(tags, tagCategories),
  }
}

export const filterUserOptionsByTags = (userOptions, tagIds) => {
  if (isEmpty(tagIds)) {
    return userOptions
  }
  const selected = new Set(tagIds)
  return userOptions.filter((user) => (user.tagIds || []).some((id) => selected.has(id)))
}

export const validateManualRows = ({ rows, labelA, labelB, autoDecide, modeLabel }) => {
  const partial = rows.some((row) => {
    const a = row[labelA]?.trim()
    const b = row[labelB]?.trim()
    return Boolean(a) !== Boolean(b)
  })
  if (partial) {
    return `Each ${modeLabel} row must include both required fields.`
  }

  if (!autoDecide && !rows.some((row) => row[labelA]?.trim() && row[labelB]?.trim())) {
    return `Add at least one ${modeLabel} row or enable Auto Decide.`
  }

  return null
}

const getTrimmedValue = (value) => value?.trim?.() || ''

const getManualPayloadRows = (rows, leftKey, rightKey, leftOutputKey, rightOutputKey) =>
  rows
    .map((row) => ({
      [leftOutputKey]: getTrimmedValue(row[leftKey]),
      [rightOutputKey]: getTrimmedValue(row[rightKey]),
    }))
    .filter((row) => row[leftOutputKey] && row[rightOutputKey])

const getPlaybookPayload = ({ formValues, playbookMode, notetakerMode }) => ({
  playbook_mode: playbookMode,
  playbook_name:
    playbookMode === 'create' ? getTrimmedValue(formValues.playbookName) || null : null,
  selected_playbook_cid: playbookMode === 'existing' ? formValues.selectedPlaybookCid : null,
  notetaker_mode: notetakerMode,
  notetaker_auto_decide: notetakerMode === 'create' ? formValues.notetakerAutoDecide : true,
  notetaker_fields:
    notetakerMode === 'create'
      ? getManualPayloadRows(
          formValues.notetakerFields,
          'fieldName',
          'prompt',
          'field_name',
          'prompt'
        )
      : [],
})

const getScorecardPayload = ({ formValues, scorecardMode }) => ({
  scorecard_mode: scorecardMode,
  scorecard_name:
    scorecardMode === 'create' ? getTrimmedValue(formValues.scorecardName) || null : null,
  selected_scorecard_config_id:
    scorecardMode === 'existing' ? formValues.selectedScorecardConfigId : null,
  scorecard_auto_decide: scorecardMode === 'create' ? formValues.scorecardAutoDecide : true,
  scorecard_criteria:
    scorecardMode === 'create'
      ? getManualPayloadRows(
          formValues.scorecardCriteria,
          'criteriaName',
          'question',
          'criteria_name',
          'question'
        )
      : [],
})

const getCallGenerationPayload = (formValues) => {
  const usesAgentResolution = formValues.generateCalls || formValues.generateOmnichannel
  return {
    generate_calls: formValues.generateCalls,
    agent_mode: formValues.agentMode,
    new_users_to_create:
      usesAgentResolution && formValues.agentMode === 'create' ? formValues.newUsersToCreate : 0,
    num_calls: formValues.generateCalls ? formValues.numCalls : 0,
    fast_mode: Boolean(formValues.generateCalls && formValues.fastMode),
    selected_usernames:
      usesAgentResolution && formValues.agentMode === 'existing'
        ? formValues.selectedUsernames
        : [],
    user_tag_ids:
      usesAgentResolution && formValues.agentMode === 'create' && formValues.newUsersToCreate > 0
        ? formValues.userTagIds
        : [],
  }
}

const getOmnichannelPayload = (formValues) => ({
  generate_omnichannel: formValues.generateOmnichannel,
  omnichannel_channels: formValues.generateOmnichannel ? formValues.omnichannelChannels : [],
  omnichannel_conversations_per_channel: formValues.generateOmnichannel
    ? formValues.omnichannelConversationsPerChannel
    : 0,
})

// Trend simulation spreads both calls and omnichannel across the window and
// auto-creates the trend; gated on generateCalls since the created trend is voice.
const getTrendPayload = (formValues) => {
  const enabled = Boolean(formValues.generateCalls && formValues.simulateTrend)
  return {
    distribute_over_days: enabled ? formValues.distributeOverDays : null,
    distribution_shape: formValues.distributionShape,
    create_trend: enabled,
  }
}

export const buildSetupPayload = ({
  formValues,
  username,
  organizationid,
  token,
  showPlaybookOptions,
  showScoringOptions,
}) => {
  const playbookMode = showPlaybookOptions ? formValues.playbookMode : 'skip'
  const notetakerMode = showPlaybookOptions ? formValues.notetakerMode : 'skip'
  const scorecardMode = showScoringOptions ? formValues.scorecardMode : 'all_active'

  return {
    auth_username: username,
    auth_organization_id: organizationid,
    auth_token: token,
    organization_id: formValues.organizationId,
    organization_name: getTrimmedValue(formValues.organizationName),
    use_case: getTrimmedValue(formValues.useCase),
    insights_goal: getTrimmedValue(formValues.insightsGoal),
    ...getCallGenerationPayload(formValues),
    ...getPlaybookPayload({ formValues, playbookMode, notetakerMode }),
    ...getScorecardPayload({ formValues, scorecardMode }),
    ...getOmnichannelPayload(formValues),
    ...getTrendPayload(formValues),
  }
}

const getTrimmedStringSchema = () =>
  Yup.string()
    .nullable()
    .transform((_, originalValue) => getTrimmedValue(originalValue))

const getManualRowSchema = (leftKey, rightKey) =>
  Yup.object().shape({
    id: Yup.mixed(),
    [leftKey]: getTrimmedStringSchema(),
    [rightKey]: getTrimmedStringSchema(),
  })

const withValidationRule = (schema, { name, message, getErrorMessage, isValid }) =>
  schema.test(name, message || '', function validate(values) {
    const currentValues = values || {}

    if (isValid(currentValues)) {
      return true
    }

    return this.createError({
      message: getErrorMessage ? getErrorMessage(currentValues) : message,
    })
  })

const getManualRowError = ({ values, rowsKey, labelA, labelB, autoDecideKey, modeLabel }) =>
  validateManualRows({
    rows: values[rowsKey] || [],
    labelA,
    labelB,
    autoDecide: values[autoDecideKey],
    modeLabel,
  })

const isExistingPlaybookMode = (values, showPlaybookOptions) =>
  showPlaybookOptions && values.playbookMode === 'existing'

const isCreateNotetakerMode = (values, showPlaybookOptions) =>
  showPlaybookOptions && values.notetakerMode === 'create'

const isExistingScorecardMode = (values, showScoringOptions) =>
  showScoringOptions && values.scorecardMode === 'existing'

const isCreateScorecardMode = (values, showScoringOptions) =>
  showScoringOptions && values.scorecardMode === 'create'

const getOrganizationValidationRules = () => [
  {
    name: 'organization-selection',
    message: 'Select an existing organization or enter a new organization name.',
    isValid: (values) => Boolean(values.organizationName) || Boolean(values.organizationId),
  },
  {
    name: 'use-case-required',
    message: 'Use case is required.',
    isValid: (values) => Boolean(values.useCase),
  },
]

const notetakerRowConfig = {
  rowsKey: 'notetakerFields',
  labelA: 'fieldName',
  labelB: 'prompt',
  autoDecideKey: 'notetakerAutoDecide',
  modeLabel: 'notetaker',
}

const getPlaybookValidationRules = ({ showPlaybookOptions }) => [
  {
    name: 'playbook-reuse-organization',
    message: 'An existing organization is required to reuse a playbook.',
    isValid: (values) =>
      !isExistingPlaybookMode(values, showPlaybookOptions) || Boolean(values.organizationId),
  },
  {
    name: 'playbook-selection',
    message: 'Select an existing playbook.',
    isValid: (values) =>
      !isExistingPlaybookMode(values, showPlaybookOptions) || Boolean(values.selectedPlaybookCid),
  },
  {
    name: 'notetaker-fields',
    getErrorMessage: (values) => getManualRowError({ values, ...notetakerRowConfig }),
    isValid: (values) =>
      !isCreateNotetakerMode(values, showPlaybookOptions) ||
      !getManualRowError({ values, ...notetakerRowConfig }),
  },
]

const scorecardRowConfig = {
  rowsKey: 'scorecardCriteria',
  labelA: 'criteriaName',
  labelB: 'question',
  autoDecideKey: 'scorecardAutoDecide',
  modeLabel: 'scorecard criterion',
}

const getScorecardValidationRules = ({ showScoringOptions }) => [
  {
    name: 'scorecard-reuse-organization',
    message: 'An existing organization is required to reuse a scorecard.',
    isValid: (values) =>
      !isExistingScorecardMode(values, showScoringOptions) || Boolean(values.organizationId),
  },
  {
    name: 'scorecard-selection',
    message: 'Select an existing Quality scorecard.',
    isValid: (values) =>
      !isExistingScorecardMode(values, showScoringOptions) ||
      Boolean(values.selectedScorecardConfigId),
  },
  {
    name: 'scorecard-criteria',
    getErrorMessage: (values) => getManualRowError({ values, ...scorecardRowConfig }),
    isValid: (values) =>
      !isCreateScorecardMode(values, showScoringOptions) ||
      !getManualRowError({ values, ...scorecardRowConfig }),
  },
]

const requiresAgentResolution = (values) => values.generateCalls || values.generateOmnichannel

const getCallGenerationValidationRules = ({ showPlaybookOptions }) => [
  {
    name: 'selected-existing-agents',
    message: 'Select at least one existing agent for these synthetic interactions',
    isValid: (values) =>
      !requiresAgentResolution(values) ||
      values.agentMode !== 'existing' ||
      !isEmpty(values.selectedUsernames),
  },
  {
    name: 'new-users-to-create',
    message: 'Create at least one new user to receive synthetic interactions.',
    isValid: (values) =>
      !requiresAgentResolution(values) ||
      values.agentMode !== 'create' ||
      values.newUsersToCreate !== 0,
  },
  {
    name: 'playbook-required-for-generation',
    message: 'Synthetic call generation requires Playbook & Notetaker to be enabled.',
    isValid: (values) => !values.generateCalls || showPlaybookOptions,
  },
  {
    name: 'non-negative-new-users',
    message: 'New users to create must be 0 or greater.',
    isValid: (values) => values.newUsersToCreate >= 0,
  },
  {
    name: 'num-calls-range',
    message: 'Number of calls must be between 1 and 100.',
    isValid: (values) =>
      !values.generateCalls ||
      (Boolean(values.numCalls) && values.numCalls >= 1 && values.numCalls <= 100),
  },
  {
    name: 'distribute-over-days-range',
    message: `Trend window must be between ${DISTRIBUTE_OVER_DAYS_MIN} and ${DISTRIBUTE_OVER_DAYS_MAX} days.`,
    isValid: (values) =>
      !values.generateCalls ||
      !values.simulateTrend ||
      (Boolean(values.distributeOverDays) &&
        values.distributeOverDays >= DISTRIBUTE_OVER_DAYS_MIN &&
        values.distributeOverDays <= DISTRIBUTE_OVER_DAYS_MAX),
  },
]

const getOmnichannelValidationRules = () => [
  {
    name: 'omnichannel-channel-selection',
    message: 'Select at least one omnichannel type (chat, sms, or email).',
    isValid: (values) => !values.generateOmnichannel || !isEmpty(values.omnichannelChannels),
  },
  {
    name: 'omnichannel-conversations-range',
    message: `Conversations per channel must be between ${OMNICHANNEL_CONVERSATIONS_MIN} and ${OMNICHANNEL_CONVERSATIONS_MAX}.`,
    isValid: (values) =>
      !values.generateOmnichannel ||
      (values.omnichannelConversationsPerChannel >= OMNICHANNEL_CONVERSATIONS_MIN &&
        values.omnichannelConversationsPerChannel <= OMNICHANNEL_CONVERSATIONS_MAX),
  },
]

export const buildSetupValidationSchema = ({ showPlaybookOptions, showScoringOptions }) => {
  const validationRules = [
    ...getOrganizationValidationRules(),
    ...getPlaybookValidationRules({ showPlaybookOptions }),
    ...getScorecardValidationRules({ showScoringOptions }),
    ...getCallGenerationValidationRules({ showPlaybookOptions }),
    ...getOmnichannelValidationRules(),
  ]

  return validationRules.reduce(
    (schema, rule) => withValidationRule(schema, rule),
    Yup.object().shape({
      organizationId: Yup.number().nullable(),
      organizationName: getTrimmedStringSchema(),
      useCase: getTrimmedStringSchema(),
      insightsGoal: getTrimmedStringSchema(),
      playbookName: getTrimmedStringSchema(),
      selectedPlaybookCid: Yup.string().nullable(),
      scorecardName: getTrimmedStringSchema(),
      selectedScorecardConfigId: Yup.number().nullable(),
      newUsersToCreate: Yup.number().nullable(),
      numCalls: Yup.number().nullable(),
      fastMode: Yup.boolean().default(false),
      simulateTrend: Yup.boolean().default(false),
      distributeOverDays: Yup.number().nullable(),
      distributionShape: Yup.string().nullable(),
      selectedUsernames: Yup.array().of(Yup.string()).default([]),
      userTagIds: Yup.array().of(Yup.number()).default([]),
      existingAgentFilterTagIds: Yup.array().of(Yup.number()).default([]),
      notetakerFields: Yup.array().of(getManualRowSchema('fieldName', 'prompt')).default([]),
      scorecardCriteria: Yup.array().of(getManualRowSchema('criteriaName', 'question')).default([]),
      omnichannelChannels: Yup.array().of(Yup.string()).default([]),
      omnichannelConversationsPerChannel: Yup.number().nullable(),
    })
  )
}
